import * as React from 'react';

import { Unit } from '@rematter/frontend-lib-api-client';
import { Col } from '@rematter/ui-web';

import { LoadStagedItemLine } from './LoadStagedItemLine';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
  WeightUnitType,
} from '~/modules/trade/types/load';

type StagedItemsListProps = {
  stagedLineItems: LoadFormLineItem[];
  disabled?: boolean;
  facilityDefaultUnit?: Unit;
  newlyStagedItemId: string | null;
  getLineItemWeightUnit: (item: LoadFormLineItem) => WeightUnitType;
  onUpdateItem: (id: string, updates: Partial<LoadFormLineItem>) => void;
  onDeleteItem: (id: string) => void;
};

const StagedItemsListComponent: React.FC<StagedItemsListProps> = ({
  stagedLineItems,
  disabled,
  facilityDefaultUnit,
  newlyStagedItemId,
  getLineItemWeightUnit,
  onUpdateItem,
  onDeleteItem,
}) => {
  const t = useTranslations();

  if (!stagedLineItems || stagedLineItems.length === 0) {
    return (
      <Col yCenter xCenter gap1 className="h-full py-5 text-sm text-neutral">
        <span className="text-lg font-semibold">
          {t.loadStaged.emptyMaterials.title}
        </span>
        <span className="text-sm text-neutral/50">
          {t.loadStaged.emptyMaterials.description}
        </span>
      </Col>
    );
  }

  return (
    <>
      {stagedLineItems.map((item) => {
        const itemWeightUnit = getLineItemWeightUnit(item);

        return (
          <LoadStagedItemLine
            key={item.id}
            lineItem={item}
            disabled={disabled}
            weightUnit={itemWeightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            onUpdateItem={onUpdateItem}
            onDeleteItem={onDeleteItem}
            autoFocus={newlyStagedItemId === item.id}
          />
        );
      })}
    </>
  );
};

export const StagedItemsList = React.memo(
  StagedItemsListComponent,
  (prevProps, nextProps) => {
    if (
      prevProps.disabled !== nextProps.disabled ||
      prevProps.facilityDefaultUnit !== nextProps.facilityDefaultUnit ||
      prevProps.newlyStagedItemId !== nextProps.newlyStagedItemId ||
      prevProps.stagedLineItems.length !== nextProps.stagedLineItems.length
    ) {
      return false;
    }

    const prevItemsMap = new Map(
      prevProps.stagedLineItems.map((item) => [item.id, item]),
    );

    for (const nextItem of nextProps.stagedLineItems) {
      const prevItem = prevItemsMap.get(nextItem.id);

      if (
        !prevItem ||
        prevItem.itemId !== nextItem.itemId ||
        prevItem.weights?.[enumLoadWeightType.Net]?.value !==
          nextItem.weights?.[enumLoadWeightType.Net]?.value ||
        prevItem.weights?.[enumLoadWeightType.Net]?.unit !==
          nextItem.weights?.[enumLoadWeightType.Net]?.unit ||
        prevItem.unitPrice?.amount !== nextItem.unitPrice?.amount
      ) {
        return false;
      }
    }

    return true;
  },
);
